/**
 * Condition parsing of the filters v2 (D528): one UNTRUSTED condition (`{ dimension, op, values, currency }`) or one HAVING condition in,
 * a validated {@link ParsedCondition} / {@link ParsedHaving} (or a stable reason code) out.
 *
 * Nothing here builds SQL. A value is validated by the dimension's type and later travels as a bound parameter with an explicit cast; a
 * number is canonicalised to an integer string (`007` is `7`, `-0` is `0`), so the SQL and the in-memory evaluator compare the same thing.
 * A text bound is any printable text of at most 64 characters: `%`, `_` and `\` are ordinary characters in it (the LIKE pattern is
 * assembled with them escaped, see `likePattern`).
 */
import { getDimension, getMetric } from './catalog.js';
import { isNumberType, isOrdinalType, operatorsFor } from './operators.js';
import { isRealDate } from './dates.js';
import { FILTER_OPS, HAVING_OPS } from './types.js';
export const FILTER_LIMITS = {
    /** All conditions of a request (`filters` plus every `anyOf` group). */
    maxConditions: 16,
    maxValues: 100,
    maxGroups: 8,
    maxHaving: 4,
    maxTextLength: 64,
};
const refuse = (code, field) => ({ ok: false, code, ...(field !== undefined ? { field } : {}) });
const ID = /^[a-z][a-z0-9_]{0,63}$/;
const CONDITION_KEYS = new Set(['dimension', 'op', 'values', 'currency']);
const HAVING_KEYS = new Set(['metric', 'op', 'values', 'currency']);
const INTEGER = /^-?\d{1,15}$/;
const DECIMAL = /^-?\d{1,12}(\.\d{1,6})?$/;
const CURRENCY = /^[A-Z]{3}$/;
/**
 * The narrowest range (the count of whole units it spans, ends included) a condition on a RECORD-LEVEL number may have, by unit (D528 review
 * P1-1): 2 items, 100 meters, 60 seconds, 100 minor units of the currency. A range tighter than this, alone or made by two one-sided bounds, singles
 * out a transaction as surely as an exact value does. (A one-sided bound is open on the other side and has no width.) It is a floor on the QUESTION, not
 * a promise about the answer: a wide range can still hold one record, which is why a distinct-people count beside it needs the personal word.
 */
export const RECORD_MIN_WIDTH = { count: 2, money: 100, duration: 60, distance: 100 };
/** Refuse a record-level range narrower than its floor: the intersection of the bounds in the AND list and in each OR group, per dimension. */
export function checkRecordRanges(top, groups) {
    const conjunctions = groups.length > 0 ? groups.map((g) => [...top, ...g]) : [top];
    for (const list of conjunctions) {
        const spans = new Map();
        for (const c of list) {
            if (c.def.recordLevel !== true)
                continue;
            const s = spans.get(c.def.id) ?? { def: c.def };
            const v = (i) => BigInt(c.values[i]);
            if (c.op === 'between') {
                s.lo = s.lo === undefined || v(0) > s.lo ? v(0) : s.lo;
                s.hi = s.hi === undefined || v(1) < s.hi ? v(1) : s.hi;
            }
            else if (c.op === 'gte' || c.op === 'gt') {
                const lo = c.op === 'gt' ? v(0) + 1n : v(0);
                if (s.lo === undefined || lo > s.lo)
                    s.lo = lo;
            }
            else if (c.op === 'lte' || c.op === 'lt') {
                const hi = c.op === 'lt' ? v(0) - 1n : v(0);
                if (s.hi === undefined || hi < s.hi)
                    s.hi = hi;
            }
            spans.set(c.def.id, s);
        }
        for (const { def, lo, hi } of spans.values()) {
            if (lo === undefined || hi === undefined)
                continue;
            const floor = RECORD_MIN_WIDTH[def.valueType];
            if (hi - lo + 1n < BigInt(floor))
                return refuse('FILTER_RANGE_TOO_NARROW', def.id);
        }
    }
    return undefined;
}
const isObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** Does `value` have the shape the dimension's keys have? (Values are then bound as parameters; this is a shape gate, not an escape.) */
export function validFilterValue(def, value) {
    if (typeof value !== 'string' || value.length === 0 || value.length > 160)
        return false;
    switch (def.valueType) {
        case 'year':
            return /^\d{4}$/.test(value);
        case 'quarter':
            return /^\d{4}-Q[1-4]$/.test(value);
        case 'month':
            return /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
        case 'day':
            return isRealDate(value);
        case 'hour':
            return /^([01]\d|2[0-3])(#2)?$/.test(value);
        case 'weekday':
            return /^[1-7]$/.test(value);
        case 'dayPart':
            return /^[A-Z]{3,12}$/.test(value);
        case 'market':
            return /^[a-z]{2}:[a-z0-9]+(-[a-z0-9]+)*$/.test(value) && value.length <= 64;
        case 'country':
            return /^[A-Z]{2}$/.test(value);
        case 'region':
            return /^[A-Z]{2}-[A-Z0-9]{1,3}$/.test(value);
        case 'currency':
            return /^[A-Z]{3}$/.test(value);
        case 'vertical':
            return /^[A-Z]{3,16}$/.test(value);
        case 'code':
            return /^[A-Z][A-Z0-9_]{1,63}$/.test(value);
        case 'ref':
            return /^[A-Za-z0-9 _.:@/+=#|~-]{1,128}$/.test(value);
        case 'orderRef':
            return /^[A-Z]{3,16}:[A-Za-z0-9._:-]{1,128}$/.test(value);
        case 'flag':
            return value === 'true' || value === 'false';
        case 'digits':
            return /^[0-9]{1,3}$/.test(value);
        case 'count':
        case 'money':
        case 'duration':
        case 'distance':
            return INTEGER.test(value);
    }
}
/** A text to search for: printable, at most {@link FILTER_LIMITS.maxTextLength} characters, no control characters. */
export function validSearchText(value) {
    if (typeof value !== 'string' || value.length === 0 || value.length > FILTER_LIMITS.maxTextLength)
        return false;
    for (let i = 0; i < value.length; i += 1) {
        const unit = value.charCodeAt(i);
        if (unit < 0x20 || unit === 0x7f || unit === 0x2028 || unit === 0x2029)
            return false;
    }
    return value.trim().length > 0;
}
/** The pattern of a `contains` / `starts_with`: the text lowercased with `\`, `%` and `_` escaped, so it only ever matches itself. */
export function likePattern(op, text) {
    const escaped = text.toLowerCase().replace(/[\\%_]/g, (c) => `\\${c}`);
    return op === 'contains' ? `%${escaped}%` : `${escaped}%`;
}
/** The lowest and highest value an ordinal bound may take (the hour of the day, the ISO weekday). */
function ordinalRange(def) {
    return def.valueType === 'hour' ? [0, 23] : [1, 7];
}
const canonicalInteger = (v) => BigInt(v).toString();
/** Number of values each operator takes: [min, max]. */
function arity(op) {
    switch (op) {
        case 'eq':
        case 'contains':
        case 'starts_with':
        case 'gte':
        case 'lte':
        case 'gt':
        case 'lt':
            return [1, 1];
        case 'between':
            return [2, 2];
        case 'in':
        case 'not_in':
            return [1, FILTER_LIMITS.maxValues];
        case 'is_null':
        case 'not_null':
            return [0, 0];
    }
}
/** Parse one untrusted condition. The dimension must exist; its type decides the operators and the values. */
export function parseCondition(raw) {
    if (!isObject(raw) || Object.keys(raw).some((k) => !CONDITION_KEYS.has(k)))
        return refuse('FILTER_INVALID', 'filters');
    if (typeof raw.dimension !== 'string' || !ID.test(raw.dimension))
        return refuse('FILTER_UNKNOWN_DIMENSION', 'filters');
    const def = getDimension(raw.dimension);
    if (def === undefined)
        return refuse('FILTER_UNKNOWN_DIMENSION', raw.dimension);
    const op = raw.op ?? 'in';
    if (typeof op !== 'string' || !FILTER_OPS.includes(op))
        return refuse('FILTER_INVALID', def.id);
    if (!operatorsFor(def).includes(op))
        return refuse('FILTER_OPERATOR_NOT_ALLOWED', def.id);
    const input = raw.values === undefined && arity(op)[1] === 0 ? [] : raw.values;
    if (!Array.isArray(input))
        return refuse('FILTER_VALUE_INVALID', def.id);
    const [min, max] = arity(op);
    if (input.length < min || input.length > max)
        return refuse('FILTER_VALUE_INVALID', def.id);
    // ---- the currency of a money bound ----
    const money = def.valueType === 'money';
    let currency;
    if (raw.currency !== undefined) {
        if (!money)
            return refuse('FILTER_CURRENCY_NOT_ALLOWED', def.id);
        if (typeof raw.currency !== 'string' || !CURRENCY.test(raw.currency))
            return refuse('FILTER_CURRENCY_INVALID', def.id);
        currency = raw.currency;
    }
    // A null test says nothing about an amount, so it needs no currency; a bound or an exact value does.
    if (money && op !== 'is_null' && op !== 'not_null' && currency === undefined)
        return refuse('FILTER_CURRENCY_REQUIRED', def.id);
    if (money && (op === 'is_null' || op === 'not_null') && currency !== undefined)
        return refuse('FILTER_CURRENCY_NOT_ALLOWED', def.id);
    // ---- the values ----
    const values = [];
    if (op === 'contains' || op === 'starts_with') {
        if (!validSearchText(input[0]))
            return refuse('FILTER_VALUE_INVALID', def.id);
        values.push(input[0].trim());
    }
    else if (op === 'between' || op === 'gte' || op === 'lte' || op === 'gt' || op === 'lt') {
        for (const v of input) {
            if (typeof v !== 'string' || !INTEGER.test(v))
                return refuse('FILTER_VALUE_INVALID', def.id);
            const canonical = canonicalInteger(v);
            if (isOrdinalType(def.valueType)) {
                const [lo, hi] = ordinalRange(def);
                if (Number(canonical) < lo || Number(canonical) > hi)
                    return refuse('FILTER_VALUE_INVALID', def.id);
            }
            else if (!isNumberType(def.valueType)) {
                return refuse('FILTER_OPERATOR_NOT_ALLOWED', def.id);
            }
            values.push(canonical);
        }
        // A range runs low to high; only the hour of the day may wrap past midnight (22 to 5).
        if (op === 'between' && def.valueType !== 'hour' && BigInt(values[0]) > BigInt(values[1]))
            return refuse('FILTER_VALUE_INVALID', def.id);
    }
    else {
        for (const v of input) {
            if (!validFilterValue(def, v))
                return refuse('FILTER_VALUE_INVALID', def.id);
            const canonical = isNumberType(def.valueType) ? canonicalInteger(v) : v;
            if (!values.includes(canonical))
                values.push(canonical);
        }
    }
    return { ok: true, value: { def, op: (op === 'eq' ? 'in' : op), values, ...(currency !== undefined ? { currency } : {}) } };
}
/** Parse one untrusted HAVING condition against the metrics the request selected. */
export function parseHaving(raw, selected) {
    if (!isObject(raw) || Object.keys(raw).some((k) => !HAVING_KEYS.has(k)))
        return refuse('HAVING_INVALID', 'having');
    if (typeof raw.metric !== 'string' || !ID.test(raw.metric))
        return refuse('HAVING_METRIC_UNKNOWN', 'having');
    const metric = getMetric(raw.metric);
    if (metric === undefined)
        return refuse('HAVING_METRIC_UNKNOWN', raw.metric);
    // `selected` is absent when a stored state is checked on its own, before any request exists to select the metric.
    if (selected !== undefined && !selected.includes(metric))
        return refuse('HAVING_METRIC_NOT_SELECTED', metric.id);
    if (typeof raw.op !== 'string' || !HAVING_OPS.includes(raw.op))
        return refuse('HAVING_INVALID', metric.id);
    const op = raw.op;
    if (!Array.isArray(raw.values) || raw.values.length !== (op === 'between' ? 2 : 1))
        return refuse('HAVING_VALUE_INVALID', metric.id);
    // A count, an amount and a distance are whole numbers; a ratio and an average duration may have decimals.
    const decimal = metric.unit === 'ratio' || metric.unit === 'duration_s' || metric.unit === 'decimal' || metric.unit === 'delta_s';
    const values = [];
    for (const v of raw.values) {
        if (typeof v !== 'string' || !(decimal ? DECIMAL : INTEGER).test(v))
            return refuse('HAVING_VALUE_INVALID', metric.id);
        values.push(canonicalDecimal(v));
    }
    if (op === 'between' && compareDecimal(values[0], values[1]) > 0)
        return refuse('HAVING_VALUE_INVALID', metric.id);
    // An amount only means something in a currency (the answer is per currency): a money metric names it, nothing else may.
    let currency;
    if (raw.currency !== undefined) {
        if (metric.unit !== 'money')
            return refuse('HAVING_CURRENCY_NOT_ALLOWED', metric.id);
        if (typeof raw.currency !== 'string' || !CURRENCY.test(raw.currency))
            return refuse('HAVING_CURRENCY_INVALID', metric.id);
        currency = raw.currency;
    }
    if (metric.unit === 'money' && currency === undefined)
        return refuse('HAVING_CURRENCY_REQUIRED', metric.id);
    return { ok: true, value: { metric, op, values, ...(currency !== undefined ? { currency } : {}) } };
}
function toDec(text) {
    const neg = text.startsWith('-');
    const body = neg ? text.slice(1) : text;
    const [whole, frac = ''] = body.split('.');
    const digits = BigInt(`${whole}${frac ?? ''}`);
    return { neg: neg && digits !== 0n, digits, scale: (frac ?? '').length };
}
/** Normalise a decimal text: no leading zeros, no trailing fractional zeros, no `-0`. */
export function canonicalDecimal(text) {
    const d = toDec(text);
    let digits = d.digits.toString().padStart(d.scale + 1, '0');
    let frac = d.scale > 0 ? digits.slice(-d.scale) : '';
    digits = digits.slice(0, digits.length - d.scale);
    frac = frac.replace(/0+$/, '');
    return `${d.neg ? '-' : ''}${digits}${frac !== '' ? `.${frac}` : ''}`;
}
/** Compare two decimal texts exactly: -1, 0 or 1. */
export function compareDecimal(a, b) {
    const x = toDec(a);
    const y = toDec(b);
    const scale = Math.max(x.scale, y.scale);
    const sx = (x.neg ? -1n : 1n) * x.digits * 10n ** BigInt(scale - x.scale);
    const sy = (y.neg ? -1n : 1n) * y.digits * 10n ** BigInt(scale - y.scale);
    return sx < sy ? -1 : sx > sy ? 1 : 0;
}
/** Does the metric value `value` satisfy `op` against the bounds? A missing value (a quotient over nothing) never does. */
export function havingHolds(op, bounds, value) {
    if (value === null || value === undefined || value === '')
        return false;
    const lo = compareDecimal(value, bounds[0]);
    switch (op) {
        case 'eq':
            return lo === 0;
        case 'gt':
            return lo > 0;
        case 'gte':
            return lo >= 0;
        case 'lt':
            return lo < 0;
        case 'lte':
            return lo <= 0;
        case 'between':
            return lo >= 0 && compareDecimal(value, bounds[1]) <= 0;
    }
}
//# sourceMappingURL=filter.js.map